import styled from 'styled-components'

const PDF_PAGE_WIDTH = 816
const PDF_PAGE_HEIGHT = 1056
const PDF_PAGE_MARGIN_VERTICAL = 56
const PDF_PAGE_MARGIN_HORIZONTAL = 48
const CLOSING_SECTION_PADDING_VERTICAL = 12
const DECLARATION_PADDING_HORIZONTAL = 80
const SIGNATURES_PADDING_HORIZONTAL = 25
const COMB_DATE_SLOT_WIDTH_PX = 18
const COMB_DATE_CELL_PADDING_PX = 10
const PERIOD_SEPARATOR_CELL_WIDTH_PX = 48
/** Offset vertical de los dígitos en celdas Año/Mes/Día (etiqueta + peine). */
const DATE_COMB_CHAR_TOP_OFFSET_PX = 20

const dateCombCellWidth = (slotCount: number) => {
  const gridWidth = slotCount * COMB_DATE_SLOT_WIDTH_PX
  const minLabelWidth = slotCount === 4 ? 30 : 24

  return Math.max(gridWidth, minLabelWidth) + COMB_DATE_CELL_PADDING_PX
}

export const TemplateRoot = styled.div`
  width: ${PDF_PAGE_WIDTH}px;
  min-height: ${PDF_PAGE_HEIGHT}px;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  line-height: 1.2;
  box-sizing: border-box;
  padding: ${PDF_PAGE_MARGIN_VERTICAL}px ${PDF_PAGE_MARGIN_HORIZONTAL}px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricPrecision;
`

export const Header = styled.div`
  text-align: center;
  margin-bottom: 12px;

  h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: #000;
  }

  h2 {
    margin: 4px 0 0;
    font-size: 13px;
    font-weight: 700;
    color: #000;
  }
`

export const Section = styled.section`
  border: 1px solid #000;
  margin-bottom: 8px;
`

export const SectionHeader = styled.div`
  background: #000;
  color: #fff;
  font-weight: 700;
  font-size: 11px;
  padding: 4px 8px;
  text-transform: uppercase;
  text-align: center;
`

export const SectionTableBody = styled.div`
  padding: 0;
`

export const SectionTableRow = styled.div`
  display: flex;
  border-bottom: 1px solid #000;

  &:last-child {
    border-bottom: none;
  }
`

export const SectionTableCell = styled.div<{
  $half?: boolean
  $flexBasis?: number
  $equal?: boolean
  $duration?: boolean
  $narrow?: boolean
  $periodIntro?: boolean
  $dateCombSlots?: number
  $centered?: boolean
  $combField?: boolean
  $periodSeparator?: boolean
}>`
  flex: ${({
    $half,
    $flexBasis,
    $equal,
    $duration,
    $narrow,
    $periodIntro,
    $dateCombSlots,
  }) => {
    if ($narrow) return `0 0 ${PERIOD_SEPARATOR_CELL_WIDTH_PX}px`
    if ($dateCombSlots != null) {
      return `0 0 ${dateCombCellWidth($dateCombSlots)}px`
    }
    if ($periodIntro) return '0 0 auto'
    if ($duration) return '1 1 0'
    if ($equal) return '1 1 0'
    if ($flexBasis != null) return `1 1 ${$flexBasis}%`
    if ($half) return '1 1 50%'
    return '1 1 100%'
  }};
  min-width: ${({ $narrow, $dateCombSlots }) => {
    if ($narrow) return `${PERIOD_SEPARATOR_CELL_WIDTH_PX}px`
    if ($dateCombSlots != null) return `${dateCombCellWidth($dateCombSlots)}px`
    return '0'
  }};
  max-width: ${({ $narrow, $dateCombSlots }) => {
    if ($narrow) return `${PERIOD_SEPARATOR_CELL_WIDTH_PX}px`
    if ($dateCombSlots != null) return `${dateCombCellWidth($dateCombSlots)}px`
    return 'none'
  }};
  padding: ${({ $narrow, $dateCombSlots }) => {
    if ($narrow) return `${DATE_COMB_CHAR_TOP_OFFSET_PX}px 4px 4px 4px`
    if ($dateCombSlots != null) return '4px 5px'
    return '4px 6px'
  }};
  border-right: 1px solid #000;
  box-sizing: border-box;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};

  ${({ $combField }) =>
    $combField &&
    `
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
  `}

  ${({ $periodSeparator }) =>
    $periodSeparator &&
    `
    display: flex;
    align-items: flex-start;
    justify-content: center;
  `}

  &:last-child {
    border-right: none;
  }
`

export const FieldLabel = styled.div`
  font-size: 9px;
  margin-bottom: 2px;
  color: #000;
`

export const FieldValue = styled.div`
  border-bottom: 1px solid #000;
  min-height: 18px;
  padding: 2px 4px;
  font-size: 11px;
  word-break: break-word;
  color: #000;
`

export const SectionFieldValue = styled(FieldValue)`
  border-bottom: none;
  padding: 2px 0;
`

export const ExecutionPeriodValue = styled(SectionFieldValue)`
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
`

export const ExecutionPeriodFromLabel = styled.span`
  margin-left: 18px;
  font-weight: 700;
  color: #000;
`

export const ExecutionPeriodSeparator = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #000;
  line-height: 1;
`

/** Peine inferior (estilo formulario STPS): solo divisiones internas, sin marco exterior. */
export const CombCharGrid = styled.div<{
  $fixedSlots?: boolean
  $centerFixedSlots?: boolean
}>`
  display: flex;
  flex: 1;
  width: ${({ $fixedSlots }) => ($fixedSlots ? 'auto' : '100%')};
  min-height: 18px;
  padding-top: 3px;
  box-sizing: border-box;
  align-items: stretch;
  align-self: ${({ $fixedSlots, $centerFixedSlots }) => {
    if (!$fixedSlots) return 'stretch'
    return $centerFixedSlots ? 'center' : 'flex-start'
  }};
`

export const CombCharSlot = styled.span<{
  $fixedSlots?: boolean
  $regular?: boolean
}>`
  position: relative;
  flex: ${({ $fixedSlots }) =>
    $fixedSlots ? `0 0 ${COMB_DATE_SLOT_WIDTH_PX}px` : '1 1 0'};
  min-width: ${({ $fixedSlots }) =>
    $fixedSlots ? `${COMB_DATE_SLOT_WIDTH_PX}px` : '0'};
  max-width: ${({ $fixedSlots }) =>
    $fixedSlots ? `${COMB_DATE_SLOT_WIDTH_PX}px` : 'none'};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  font-size: 11px;
  font-weight: 400;
  text-transform: ${({ $regular }) => ($regular ? 'none' : 'uppercase')};
  color: #000;
  line-height: 1;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    height: 100%;
    border-right: 1px solid #000;
  }
`

export const ClosingSection = styled.section`
  border: 1px solid #000;
  margin-bottom: 8px;
`

export const ClosingSectionBody = styled.div`
  padding: ${CLOSING_SECTION_PADDING_VERTICAL}px 0;
`

export const Declaration = styled.p`
  margin: 0 0 24px;
  padding: 0 ${DECLARATION_PADDING_HORIZONTAL}px;
  font-size: 9px;
  font-weight: 700;
  text-align: center;
  color: #000;
  line-height: 1.35;
`

export const SignaturesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 8px ${SIGNATURES_PADDING_HORIZONTAL}px 0;
`

export const SignatureBlock = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const SignatureRoleLabel = styled.div`
  font-size: 9px;
  font-weight: 400;
  color: #000;
  line-height: 1.3;
`

export const SignatureImageArea = styled.div`
  width: 100%;
  min-height: 64px;
  margin: 20px 0 8px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

export const SignatureImage = styled.img`
  max-width: 100%;
  max-height: 64px;
  object-fit: contain;
`

export const SignatureName = styled.div`
  font-size: 9px;
  font-weight: 700;
  margin-bottom: 4px;
  color: #000;
  text-transform: uppercase;
  line-height: 1.3;
  word-break: break-word;
`

export const SignatureLine = styled.div`
  width: 100%;
  border-top: 1px solid #000;
  margin-bottom: 4px;
`

export const SignatureFooter = styled.div`
  font-size: 9px;
  color: #000;
  line-height: 1.3;
`

export const HiddenRenderRoot = styled.div`
  position: absolute;
  left: -9999px;
  top: 0;
  width: ${PDF_PAGE_WIDTH}px;
  overflow: visible;
  pointer-events: none;
`
